import React, { useState } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Tab, MenuItem, Select, FormControl, InputLabel, IconButton
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GrassIcon from '@mui/icons-material/Grass';
import * as XLSX from 'xlsx';
import { API_URL } from '../../config';
import WhatsAppSendButton from '../common/WhatsAppSendButton';

const GREEN  = '#1b4332';
const ORANGE = '#f59e0b';

const HDR_SX = {
  fontWeight: 700, color: '#fff', fontSize: '0.8rem', textTransform: 'uppercase',
  background: `linear-gradient(135deg, ${ORANGE} 0%, #ffa726 100%)`,
  borderRight: '1px solid rgba(255,255,255,0.2)', py: 1.5, px: 1,
};
const CELL_SX = { py: 1.2, px: 1, fontSize: '0.82rem' };

function fmtNum(v, d = 0) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toFixed(d);
}

function isGrandTotal(row) {
  return String(row.QUALITY || row.ASSEMBALY_TABLENO || '').trim().toUpperCase().startsWith('GRAND TOTAL');
}

function isOverallTotal(row) {
  return (row.COMPANY || '').toUpperCase() === 'OVERALL';
}

// ─── Prod table (RS 0-3) ─────────────────────────────────────────────────────
function ProdTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data</Alert>;
  return (
    <TableContainer sx={{ maxHeight: 520, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 700 }}>
        <TableHead>
          <TableRow>
            {['QUALITY','LENGTH','WIDTH','THICKNESS','PCS','CBM'].map(h => (
              <TableCell key={h} sx={{ ...HDR_SX, textAlign: 'center' }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const grand = isGrandTotal(row);
            return (
              <TableRow key={i} sx={{
                backgroundColor: grand ? '#fef3c7' : (i % 2 === 0 ? '#fff' : '#f8faf9'),
                '&:hover': { backgroundColor: grand ? '#fef3c7' : '#e8f5e9' },
              }}>
                <TableCell sx={{ ...CELL_SX, textAlign: 'center', fontWeight: grand ? 700 : 500, color: grand ? '#92400e' : '#333' }}>{row.QUALITY || '—'}</TableCell>
                <TableCell sx={{ ...CELL_SX, textAlign: 'center', color: grand ? '#92400e' : '#555' }}>{fmtNum(row.LENGTH, 3)}</TableCell>
                <TableCell sx={{ ...CELL_SX, textAlign: 'center', color: grand ? '#92400e' : '#555' }}>{fmtNum(row.WIDTH, 3)}</TableCell>
                <TableCell sx={{ ...CELL_SX, textAlign: 'center', color: grand ? '#92400e' : '#555' }}>{fmtNum(row.THICKNESS, 2)}</TableCell>
                <TableCell sx={{ ...CELL_SX, textAlign: 'center', fontWeight: grand ? 700 : 600, color: grand ? '#92400e' : '#2e7d32' }}>{fmtNum(row.PCS, 0)}</TableCell>
                <TableCell sx={{ ...CELL_SX, textAlign: 'center', fontWeight: grand ? 700 : 600, color: grand ? '#92400e' : '#2e7d32' }}>{fmtNum(row.CBM, 3)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Table-wise summary (RS 4) — per company ─────────────────────────────────
function TableSummary({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data</Alert>;

  const getRowStyle = (row, i) => {
    const overall = isOverallTotal(row);
    const grand   = isGrandTotal(row);
    const company = (row.COMPANY || '').toUpperCase();

    if (overall)                          return { bg: GREEN,     color: '#fff',     fw: 700 };
    if (grand && company === 'STAR-1')    return { bg: '#dbeafe', color: '#1e40af',  fw: 700 };
    if (grand && company === 'STAR-2')    return { bg: '#dcfce7', color: '#166534',  fw: 700 };
    if (grand)                            return { bg: '#fef3c7', color: '#92400e',  fw: 700 };
    if (company === 'STAR-1')             return { bg: i % 2 === 0 ? '#fff' : '#eff6ff', color: '#333', fw: 500 };
    if (company === 'STAR-2')             return { bg: i % 2 === 0 ? '#fff' : '#f0fdf4', color: '#333', fw: 500 };
    return                                       { bg: i % 2 === 0 ? '#fff' : '#f8faf9', color: '#333', fw: 500 };
  };

  return (
    <TableContainer sx={{ maxHeight: 520, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 450 }}>
        <TableHead>
          <TableRow>
            {['COMPANY', 'TABLE NO', 'PCS', 'CBM'].map(h => (
              <TableCell key={h} sx={{ ...HDR_SX, textAlign: 'center' }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const { bg, color, fw } = getRowStyle(row, i);
            const overall = isOverallTotal(row);
            return (
              <TableRow key={i} sx={{ backgroundColor: bg, '&:hover': { filter: 'brightness(0.97)' } }}>
                <TableCell sx={{ ...CELL_SX, textAlign: 'center', fontWeight: fw, color }}>
                  {overall ? 'OVERALL' : (row.COMPANY || '—')}
                </TableCell>
                <TableCell sx={{ ...CELL_SX, textAlign: 'center', fontWeight: fw, color }}>{row.ASSEMBALY_TABLENO || '—'}</TableCell>
                <TableCell sx={{ ...CELL_SX, textAlign: 'center', fontWeight: fw, color }}>{fmtNum(row.PCS, 0)}</TableCell>
                <TableCell sx={{ ...CELL_SX, textAlign: 'center', fontWeight: fw, color }}>{fmtNum(row.CBM, 3)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const TAB_LABELS = [
  'Star-1 MAT & Direct', 'Star-1 Packing-PLY',
  'Star-2 MAT & Direct', 'Star-2 Packing-PLY',
  'Table-wise Summary',
];

const DailyMatProductionView = ({ onBackClick, user }) => {
  const [date, setDate]       = useState(null);
  const [shift, setShift]     = useState('DAY');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [data, setData]       = useState(null);
  const [tab, setTab]         = useState(0);

  // Merge two sets of recordsets (DAY + NIGHT) for BOTH mode.
  // RS 0-3: concatenate detail rows, recalculate grand total at end.
  // RS 4: merge by COMPANY+TABLE, recalculate per-company and overall totals.
  const mergeRecordsets = (rsA, rsB) => {
    const merged = [];

    // RS 0-3: prod tables — strip grand total rows, merge details, re-add grand total
    for (let i = 0; i < 4; i++) {
      const a = (rsA[i] || []).filter(r => !isGrandTotal(r));
      const b = (rsB[i] || []).filter(r => !isGrandTotal(r));

      // Group by QUALITY+LENGTH+WIDTH+THICKNESS and sum
      const map = {};
      [...a, ...b].forEach(row => {
        const key = `${row.QUALITY}|${row.LENGTH}|${row.WIDTH}|${row.THICKNESS}`;
        if (!map[key]) map[key] = { ...row, PCS: 0, CBM: 0 };
        map[key].PCS += parseFloat(row.PCS) || 0;
        map[key].CBM += parseFloat(row.CBM) || 0;
      });

      const details = Object.values(map);
      const totalPCS = details.reduce((s, r) => s + r.PCS, 0);
      const totalCBM = details.reduce((s, r) => s + r.CBM, 0);
      merged.push([
        ...details,
        { QUALITY: 'GRAND TOTAL : ', LENGTH: null, WIDTH: null, THICKNESS: null, PCS: totalPCS, CBM: totalCBM }
      ]);
    }

    // RS 4: table-wise summary — strip all total rows, merge details, re-add totals
    const a4 = (rsA[4] || []).filter(r => !isGrandTotal(r) && !isOverallTotal(r));
    const b4 = (rsB[4] || []).filter(r => !isGrandTotal(r) && !isOverallTotal(r));
    const map4 = {};
    [...a4, ...b4].forEach(row => {
      const key = `${row.COMPANY}|${row.ASSEMBALY_TABLENO}`;
      if (!map4[key]) map4[key] = { COMPANY: row.COMPANY, ASSEMBALY_TABLENO: row.ASSEMBALY_TABLENO, PCS: 0, CBM: 0 };
      map4[key].PCS += parseFloat(row.PCS) || 0;
      map4[key].CBM += parseFloat(row.CBM) || 0;
    });

    const details4 = Object.values(map4).sort((a, b) =>
      a.COMPANY.localeCompare(b.COMPANY) || a.ASSEMBALY_TABLENO.localeCompare(b.ASSEMBALY_TABLENO)
    );

    // Build per-company grand totals
    const companyTotals = {};
    details4.forEach(r => {
      if (!companyTotals[r.COMPANY]) companyTotals[r.COMPANY] = { PCS: 0, CBM: 0 };
      companyTotals[r.COMPANY].PCS += r.PCS;
      companyTotals[r.COMPANY].CBM += r.CBM;
    });

    // Interleave: detail rows for each company, then that company's grand total
    const rs4 = [];
    const companies = [...new Set(details4.map(r => r.COMPANY))].sort();
    companies.forEach(company => {
      details4.filter(r => r.COMPANY === company).forEach(r => rs4.push(r));
      rs4.push({ COMPANY: company, ASSEMBALY_TABLENO: 'GRAND TOTAL : ', PCS: companyTotals[company].PCS, CBM: companyTotals[company].CBM });
    });

    const overallPCS = details4.reduce((s, r) => s + r.PCS, 0);
    const overallCBM = details4.reduce((s, r) => s + r.CBM, 0);
    rs4.push({ COMPANY: 'OVERALL', ASSEMBALY_TABLENO: 'GRAND TOTAL : ', PCS: overallPCS, CBM: overallCBM });

    merged.push(rs4);
    return merged;
  };

  const fetchData = async () => {
    if (!date) { setError('Please select a date'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      if (shift === 'BOTH') {
        const [resDay, resNight] = await Promise.all([
          fetch(`${API_URL}/order/dailymatproduction`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ date: date.format('YYYY-MM-DD'), shift: 'DAY' }),
          }),
          fetch(`${API_URL}/order/dailymatproduction`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ date: date.format('YYYY-MM-DD'), shift: 'NIGHT' }),
          }),
        ]);
        if (!resDay.ok || !resNight.ok) throw new Error('API error fetching DAY or NIGHT data');
        const [dayData, nightData] = await Promise.all([resDay.json(), resNight.json()]);
        const merged = mergeRecordsets(dayData.data || [], nightData.data || []);
        if (merged.every(rs => rs.length === 0)) {
          setError('No data found for the selected date.');
          return;
        }
        setData(merged);
      } else {
        const res = await fetch(`${API_URL}/order/dailymatproduction`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ date: date.format('YYYY-MM-DD'), shift }),
        });
        if (!res.ok) throw new Error('API error: ' + res.statusText);
        const apiData = await res.json();
        if (!apiData.data || apiData.data.every(rs => rs.length === 0)) {
          setError('No data found for the selected date and shift.');
          return;
        }
        setData(apiData.data);
      }
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    TAB_LABELS.forEach((label, i) => {
      if (data[i]?.length) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data[i]), label.substring(0, 31));
      }
    });
    XLSX.writeFile(wb, `Mat_Production_${shift}_${date.format('YYYY-MM-DD')}.xlsx`);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(27,67,50,0.1)', border: '1px solid #e8f5e9' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={3}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: GREEN }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{ background: `linear-gradient(135deg, ${GREEN} 0%, #2d6a4f 100%)`, borderRadius: 2, p: 1.5, mr: 2 }}>
              <GrassIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: GREEN }}>Daily Mat Production Summary</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>By date and shift</Typography>
            </Box>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="center">
              <DatePicker label="Date" value={date} onChange={setDate}
                slotProps={{ textField: { fullWidth: true, sx: { flex: 1 } } }} />
              <FormControl sx={{ minWidth: 160 }}>
                <InputLabel>Shift</InputLabel>
                <Select value={shift} label="Shift" onChange={e => setShift(e.target.value)}>
                  <MenuItem value="DAY">DAY</MenuItem>
                  <MenuItem value="NIGHT">NIGHT</MenuItem>
                  <MenuItem value="BOTH">BOTH (DAY + NIGHT)</MenuItem>
                </Select>
              </FormControl>
              <Button variant="contained" size="large" startIcon={<SearchIcon />} onClick={fetchData}
                disabled={loading} sx={{ minWidth: 140, backgroundColor: GREEN, '&:hover': { backgroundColor: '#0f2419' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                Search
              </Button>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {loading && <Box textAlign="center" py={6}><CircularProgress sx={{ color: GREEN }} /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #e8f5e9' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 1 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} textColor="inherit"
              sx={{ '& .MuiTab-root': { fontWeight: 600, fontSize: '0.8rem' }, '& .Mui-selected': { color: GREEN }, '& .MuiTabs-indicator': { backgroundColor: GREEN } }}>
              {TAB_LABELS.map((l, i) => <Tab key={i} label={l} />)}
            </Tabs>
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownload}
              sx={{ color: GREEN, fontWeight: 600, textTransform: 'none' }}>Excel</Button>
          </Box>
          {[0,1,2,3].includes(tab) && <ProdTable rows={data[tab]} />}
          {tab === 4 && <TableSummary rows={data[4]} />}
        </Card>
      )}

      {/* WhatsApp send button — hidden for BOTH shift (view-only) */}
      {data && shift !== 'BOTH' && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
          <WhatsAppSendButton
            user={user}
            label={`Send Mat ${shift} Shift to WhatsApp`}
            apiUrl={date ? `${API_URL}/api/mat/test-${shift.toLowerCase()}?date=${date.format('YYYY-MM-DD')}` : null}
            disabled={!date}
          />
        </Box>
      )}
    </Container>
  );
};

export default DailyMatProductionView;
